import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useSessionStore } from '../../lib/auth/sessionStore';

/**
 * Gates the admin tree.
 *
 * Waits for the boot-time refresh to settle before deciding. Without that wait, every
 * page reload would bounce a signed-in owner to the login form for a frame — the access
 * token is deliberately held only in memory, so it is always absent at first paint and
 * only the httpOnly cookie can bring the session back.
 */
export function RequireAuth({ children }: { readonly children: ReactNode }) {
  const session = useSessionStore((state) => state.session);
  const isRestoring = useSessionStore((state) => state.isRestoring);
  const location = useLocation();

  if (isRestoring) {
    return (
      <main className="grid min-h-dvh place-items-center px-6">
        <p className="text-muted">Checking your session…</p>
      </main>
    );
  }

  if (session === null) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  // The API refuses every non-auth endpoint while this is set, so routing anywhere else
  // would land on a 422 the user cannot act on from that screen.
  if (session.mustChangePassword && location.pathname !== '/admin/change-password') {
    return <Navigate to="/admin/change-password" replace />;
  }

  return <>{children}</>;
}
